import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/SeoLanding.css';

const SeoLandingScreen = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add('visible'), i * 60);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    
    reveals.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="seo-landing-container">
      {/* ─── NAV ─── */}
      <nav>
        <Link to="/" className="nav-logo">
          <div className="nav-logo-icon">
            <svg viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          </div>
          <span className="nav-logo-text">JobSkills</span>
        </Link>
        <ul className="nav-links">
          <li><Link to="/how-it-works" className="active">How it Works</Link></li>
          <li><Link to="/search">Start Interview</Link></li>
          <li><Link to="/blog">Blog</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
        </ul>
        <Link to="/search" className="nav-cta">Try for free</Link>
      </nav>

      {/* ─── HERO ─── */}
      <section className="hero">
        <div className="hero-badge">
          <span className="badge-dot"></span>
          50,000+ mock interviews completed
        </div>
        <h1>Practice Job Interviews with <em>AI</em> — and Actually Get Hired</h1>
        <p className="hero-sub">
          AI asks you real interview questions. You answer. Get instant BARS-scored feedback that tells you <strong>exactly</strong> what to improve — like a real interview coach.
        </p>
        <div className="hero-actions">
          <Link to="/search" className="btn-primary">Start free practice →</Link>
          <Link to="/how-it-works" className="btn-secondary">See how it works</Link>
        </div>
        <p className="hero-note">No credit card needed · <strong>3 free sessions</strong> · Results in under 5 minutes</p>
      </section>

      {/* ─── STATS ─── */}
      <div className="stats-bar">
        <div className="stat-item"><span className="stat-num">10K+</span><span className="stat-label">Jobs in database</span></div>
        <div className="stat-item"><span className="stat-num">50K+</span><span className="stat-label">Interviews practiced</span></div>
        <div className="stat-item"><span className="stat-num">85%</span><span className="stat-label">Users get callback</span></div>
        <div className="stat-item"><span className="stat-num">4.8★</span><span className="stat-label">Average rating</span></div>
      </div>

      {/* ─── HOW IT WORKS ─── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header center reveal">
            <span className="section-tag">How it works</span>
            <h2 className="section-title">From nervous to <em>confident</em> in 4 steps</h2>
            <p className="section-sub">JobSkills mirrors a real interview — from question selection to structured feedback</p>
          </div>
          <div className="steps">
            <div className="step-card reveal">
              <span className="step-num">01</span>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </div>
              <h3>Paste your job posting</h3>
              <p>JobSkills reads the role and generates interview questions tailored to that exact position.</p>
            </div>
            <div className="step-card reveal">
              <span className="step-num">02</span>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3>Answer like in a real interview</h3>
              <p>Type or speak your answers. The AI interviewer listens, asks follow-up questions, and keeps you on track.</p>
            </div>
            <div className="step-card reveal">
              <span className="step-num">03</span>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <h3>Get BARS-scored feedback</h3>
              <p>Each answer is scored on the Behaviorally Anchored Rating Scale — the same framework real recruiters use at top companies.</p>
            </div>
            <div className="step-card reveal">
              <span className="step-num">04</span>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <h3>Track progress, get hired</h3>
              <p>Your scores improve with each session. See exactly which competencies to work on before your real interview day.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURE: BARS FEEDBACK ─── */}
      <section className="section">
        <div className="container">
          <div className="split reveal">
            <div>
              <span className="section-tag">Real feedback</span>
              <h2 className="section-title">Not just "good answer." <em>Exactly</em> what to fix.</h2>
              <p className="section-sub">Most mock interview tools just say "nice job." JobSkills shows you your score per competency and explains why — so you actually improve.</p>
              <ul className="feature-list">
                <li>
                  <div className="feature-check"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></div>
                  <span><strong>BARS scoring</strong> — the same rubric used by FAANG, consulting, and Fortune 500 recruiters</span>
                </li>
                <li>
                  <div className="feature-check"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></div>
                  <span><strong>Competency breakdown</strong> — Communication, Problem-solving, Leadership, Cultural Fit</span>
                </li>
                <li>
                  <div className="feature-check"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></div>
                  <span><strong>Example of a stronger answer</strong> — see what a 5/5 response looks like for your question</span>
                </li>
                <li>
                  <div className="feature-check"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></div>
                  <span><strong>Progress tracking</strong> — your scores across sessions so you can see the improvement</span>
                </li>
              </ul>
            </div>
            <div className="split-visual">
              <div className="mock-ui">
                <div className="mock-header">
                  <div className="mock-avatar">AI</div>
                  <div className="mock-header-text">
                    <span className="mock-header-name">AI Interviewer</span>
                    <span className="mock-header-role">Senior Product Manager role · Google</span>
                  </div>
                </div>
                <div>
                  <div className="mock-bubble-label">Question</div>
                  <div className="mock-bubble">Tell me about a time you had to make a critical product decision with incomplete data. What was your process?</div>
                </div>
                <div>
                  <div className="mock-bubble-label" style={{textAlign:'right'}}>Your answer</div>
                  <div className="mock-bubble user">At my previous company, we had 3 days to decide whether to delay our launch or ship with a known UX issue. I ran a quick usability test with 5 users...</div>
                </div>
                <div>
                  <div className="mock-bubble-label">Feedback</div>
                  <div style={{background:'#F0FDF4', borderRadius: '4px 14px 14px 14px', padding:'16px 18px', borderLeft: '3px solid #22c55e'}}>
                    <div style={{fontSize:'13px', fontWeight:700, color:'#16A34A', marginBottom:'8px'}}>Score: 4.2 / 5.0</div>
                    <div style={{fontSize:'13.5px', color:'#374151', marginBottom:'12px'}}>Strong use of STAR structure. Add quantified outcome — what was the result of your decision?</div>
                    <div className="mock-score-row">
                      <div className="score-badge"><div className="score-dot green"></div> Communication 4/5</div>
                      <div className="score-badge"><div className="score-dot blue"></div> Reasoning 4/5</div>
                      <div className="score-badge"><div className="score-dot yellow"></div> Impact 3/5</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── QUESTION TYPES ─── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header center reveal">
            <span className="section-tag">Coverage</span>
            <h2 className="section-title">Every question type. Every <em>industry.</em></h2>
            <p className="section-sub">JobSkills covers the full spectrum of interview formats — behavioral, technical, situational, and more</p>
          </div>
          <div className="question-types">
            <div className="q-card reveal">
              <span className="q-tag behavioral">Behavioral</span>
              <h3>Behavioral Questions</h3>
              <p>STAR-method questions about past experiences. The most common format at top employers.</p>
              <div className="q-example">"Tell me about a time you led a team through a difficult situation."</div>
            </div>
            <div className="q-card reveal">
              <span className="q-tag situational">Situational</span>
              <h3>Situational Questions</h3>
              <p>Hypothetical scenarios testing your judgment and decision-making under pressure.</p>
              <div className="q-example">"If you discovered your team was missing a key deadline, what would you do?"</div>
            </div>
            <div className="q-card reveal">
              <span className="q-tag technical">Technical</span>
              <h3>Role-Specific</h3>
              <p>Questions tailored to your target role — PM, engineer, analyst, sales, or any other domain.</p>
              <div className="q-example">"Walk me through how you'd prioritize a feature backlog with 3 competing stakeholders."</div>
            </div>
            <div className="q-card reveal">
              <span className="q-tag motivational">Motivational</span>
              <h3>Motivational Fit</h3>
              <p>Questions about your goals, values, and why you want this role at this company.</p>
              <div className="q-example">"Why are you leaving your current position? What are you looking for next?"</div>
            </div>
            <div className="q-card reveal">
              <span className="q-tag culture">Culture Fit</span>
              <h3>Culture & Values</h3>
              <p>Questions that assess alignment with company values and working style preferences.</p>
              <div className="q-example">"Describe the environment where you do your best work."</div>
            </div>
            <div className="q-card reveal">
              <span className="q-tag comp">Competency</span>
              <h3>Competency-Based</h3>
              <p>Structured questions that map directly to the competencies on the job description.</p>
              <div className="q-example">"Give me an example of a time you demonstrated strong analytical thinking."</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="section">
        <div className="container">
          <div className="section-header center reveal">
            <span className="section-tag">Social proof</span>
            <h2 className="section-title">Real people. <em>Real offers.</em></h2>
          </div>
          <div className="testimonials">
            <div className="testimonial reveal">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="star" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                ))}
              </div>
              <p className="testimonial-text">"I had 6 failed interviews before JobSkills. After 2 weeks of practice, I finally understood <em>why</em> I was failing — my answers had no structure. Got an offer at Stripe."</p>
              <div className="testimonial-author">
                <div className="author-avatar">AK</div>
                <div className="author-info">
                  <strong>Alex K.</strong>
                  <span>Software Engineer</span>
                </div>
                <span className="company-tag">→ Stripe</span>
              </div>
            </div>
            <div className="testimonial reveal">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="star" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                ))}
              </div>
              <p className="testimonial-text">"The BARS feedback is incredible. Instead of vague 'be more specific,' it shows me the exact competency I'm weak on. Used it for my McKinsey interviews. Made it."</p>
              <div className="testimonial-author">
                <div className="author-avatar orange">SM</div>
                <div className="author-info">
                  <strong>Sarah M.</strong>
                  <span>Business Analyst</span>
                </div>
                <span className="company-tag">→ McKinsey</span>
              </div>
            </div>
            <div className="testimonial reveal">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="star" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                ))}
              </div>
              <p className="testimonial-text">"As a non-native English speaker, I was terrified of interviews. JobSkills let me practice 30+ times without judgment. Landed my first role at a US company remotely."</p>
              <div className="testimonial-author">
                <div className="author-avatar teal">JW</div>
                <div className="author-info">
                  <strong>Ji-ho W.</strong>
                  <span>Product Manager</span>
                </div>
                <span className="company-tag">→ Notion</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header center reveal">
            <span className="section-tag">Pricing</span>
            <h2 className="section-title">Start free. <em>Upgrade when ready.</em></h2>
            <p className="section-sub">Practice at your own pace. No subscriptions until you're sure it works for you.</p>
          </div>
          <div className="pricing-cards reveal">
            <div className="pricing-card">
              <div className="plan-name">Free</div>
              <div className="plan-price">$0</div>
              <div className="plan-desc">Try it. See if it works. No strings.</div>
              <ul className="plan-features">
                <li>3 mock interview sessions</li>
                <li>BARS feedback on all answers</li>
                <li>Behavioral question bank</li>
                <li>Basic progress tracking</li>
              </ul>
              <Link to="/search" className="plan-btn outline" style={{textAlign:'center', textDecoration:'none', display:'block'}}>Start free</Link>
            </div>
            <div className="pricing-card featured">
              <div className="featured-label">Most popular</div>
              <div className="plan-name">Pro</div>
              <div className="plan-price">$14.99 <span>/month</span></div>
              <div className="plan-desc">For active job seekers preparing seriously.</div>
              <ul className="plan-features">
                <li>Unlimited mock sessions</li>
                <li>Full question library (all types)</li>
                <li>Role-specific question sets</li>
                <li>Full progress dashboard</li>
                <li>Sample strong answers</li>
              </ul>
              <Link to="/pricing" className="plan-btn primary" style={{textAlign:'center', textDecoration:'none', display:'block'}}>Get Pro →</Link>
            </div>
            <div className="pricing-card">
              <div className="plan-name">Career</div>
              <div className="plan-price">$49 <span>/month</span></div>
              <div className="plan-desc">For serious candidates targeting competitive roles.</div>
              <ul className="plan-features">
                <li>Everything in Pro</li>
                <li>Company-specific question sets</li>
                <li>Resume-matched questions</li>
                <li>Priority support</li>
                <li>Interview debrief reports</li>
              </ul>
              <Link to="/pricing" className="plan-btn outline" style={{textAlign:'center', textDecoration:'none', display:'block'}}>Get Career</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="section">
        <div className="container">
          <div className="section-header center reveal">
            <span className="section-tag">FAQ</span>
            <h2 className="section-title">Questions people actually <em>ask</em></h2>
          </div>
          <div className="faq-list reveal">
            {[
              {
                q: "How is JobSkills different from just practicing with ChatGPT?",
                a: "ChatGPT gives you generic feedback. JobSkills uses the BARS framework — the actual rubric recruiters at top companies use — so you know exactly where you score and why. It also tracks your progress across sessions, something ChatGPT can't do. Plus, our questions are tailored to specific job postings, not random examples."
              },
              {
                q: "Do I need to create an account to try it?",
                a: "Yes — a free account is required so we can save your progress and feedback history. Registration takes under 30 seconds with Google sign-in. You get 3 full mock sessions completely free without entering a credit card."
              },
              {
                q: "What types of roles and industries does it cover?",
                a: "JobSkills covers tech (software engineers, PMs, data scientists), business and consulting, sales and marketing, finance, and general roles. If you paste a real job description, the AI generates questions specifically for that role — so it works for almost any position."
              },
              {
                q: "What exactly is BARS scoring? Do real companies use it?",
                a: "BARS stands for Behaviorally Anchored Rating Scale. It's a structured scoring system used by recruiters at Amazon, Google, Microsoft, McKinsey, and many others to evaluate candidates consistently. Each competency (communication, problem-solving, leadership, etc.) is scored 1–5 with specific behavioral anchors defining each level. JobSkills uses this same framework so your practice scores reflect real hiring standards."
              },
              {
                q: "Can I practice for behavioral interviews specifically?",
                a: "Absolutely — behavioral interviews are our core focus. You can practice STAR-method answers, get feedback on your story structure, and see exactly which elements (Situation, Task, Action, Result) are strong or missing. Behavioral prep is included in all plans including the free tier."
              },
              {
                q: "How many sessions do I need before seeing improvement?",
                a: "Most users see meaningful score improvement within 3–5 sessions (about 1 week of practice). Users who practice daily for 2 weeks consistently report feeling significantly more confident in real interviews. Your progress dashboard tracks your score trend so you can see the improvement over time."
              }
            ].map((item, i) => (
              <div className="faq-item" key={i}>
                <button className="faq-q" onClick={() => toggleFaq(i)}>
                  {item.q}
                  <svg className={`faq-icon ${openFaq === i ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <div className={`faq-a ${openFaq === i ? 'open' : ''}`}>{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTENT CLUSTER (SEO LINKS) ─── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header center reveal">
            <span className="section-tag">Interview guides</span>
            <h2 className="section-title">Free resources to <em>prepare faster</em></h2>
          </div>
          <div className="content-grid">
            <Link to="/blog" className="content-card reveal">
              <div className="content-card-img blue-bg">📋</div>
              <div className="content-card-body">
                <span className="content-tag">Guide</span>
                <h3>STAR Method: 25 Real Answer Examples for 2026</h3>
                <p>Complete breakdown of the STAR method with examples for common behavioral questions.</p>
                <div className="content-read">Read guide →</div>
              </div>
            </Link>
            <Link to="/blog" className="content-card reveal">
              <div className="content-card-img orange-bg">🎯</div>
              <div className="content-card-body">
                <span className="content-tag">List</span>
                <h3>50 Behavioral Interview Questions You'll Actually Be Asked</h3>
                <p>The most common behavioral questions by company type, with what interviewers are really looking for.</p>
                <div className="content-read">Read guide →</div>
              </div>
            </Link>
            <Link to="/blog" className="content-card reveal">
              <div className="content-card-img green-bg">💡</div>
              <div className="content-card-body">
                <span className="content-tag">Tips</span>
                <h3>How to Make the Most of Mock Interview Practice (Science-Backed)</h3>
                <p>Research on deliberate practice applied to interview prep — how to improve 3x faster.</p>
                <div className="content-read">Read guide →</div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="cta-section">
        <div className="container">
          <h2>Your next interview is<br/><em>closer than you think.</em></h2>
          <p>Start practicing today — get real feedback, not just encouragement.</p>
          <div className="cta-actions">
            <Link to="/search" className="btn-white">Start free practice →</Link>
            <Link to="/pricing" className="btn-ghost">See all plans</Link>
          </div>
          <p className="cta-note">3 free sessions · No credit card · Cancel anytime</p>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer>
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <Link to="/" className="nav-logo" style={{textDecoration:'none'}}>
                <div className="nav-logo-icon">
                  <svg viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="white"/></svg>
                </div>
                <span className="nav-logo-text" style={{color: 'white'}}>JobSkills</span>
              </Link>
              <p>AI-powered interview practice with BARS-scored feedback. Practice until you're ready — then go get the offer.</p>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <ul>
                <li><Link to="/how-it-works">How it Works</Link></li>
                <li><Link to="/search">Start Interview</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/chats">My Interviews</Link></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Resources</h4>
              <ul>
                <li><Link to="/blog">Interview Blog</Link></li>
                <li><Link to="/blog">STAR Method Guide</Link></li>
                <li><Link to="/blog">Behavioral Questions</Link></li>
                <li><Link to="/blog">Mock Interview Tips</Link></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li><Link to="/contacts">About</Link></li>
                <li><Link to="/terms">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><a href="mailto:hello@jobskills.work">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 JobSkills. All rights reserved.</p>
            <p>Made for people who want the job, not just the interview.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeoLandingScreen;
